import { useMemo } from 'react';
import {
  SelectedData,
  WorkDetailSimpleData,
  WorkSimpleWithDetails,
} from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ModalType, ProjectElementType } from '../../shared/enum/EnumRepository';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import {
  CAROUSEL_MAX_ITEMS,
  getCarouselImportCapacity,
  getCarouselItemSource,
} from '../../shared/util/carouselUtils';
import { useImportStationWorkStore } from '../../shared/store/importStationWorkStore';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { clearFormationSlotImportModalState } from './useFormationSlotImport';

export function useCarouselSlotImport() {
  const { standardModal, clearModal } = useModalStore();
  const updateCarouselSlotReference = useProjectElementListStore((s) => s.updateCarouselSlotReference);
  const importCarouselSelections = useProjectElementListStore((s) => s.importCarouselSelections);
  const removeCarouselItemsByIds = useProjectElementListStore((s) => s.removeCarouselItemsByIds);
  const selectedDataList = useImportStationWorkStore((s) => s.selectedDataList);
  const removedCarouselItemIds = useImportStationWorkStore((s) => s.removedCarouselItemIds);
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const modalType = standardModal?.modalType;
  const isActive = modalType === ModalType.IMPORT_STATION_FOR_CAROUSEL
    || modalType === ModalType.IMPORT_STATION_FOR_CAROUSEL_SLOT;
  const isBulkImport = modalType === ModalType.IMPORT_STATION_FOR_CAROUSEL;
  const carouselItems = useProjectElementListStore((s) => {
    const pe = s.projectElementList.find((item) => item.id === standardModal?.title);
    return pe?.projectElementType === ProjectElementType.CAROUSEL ? pe.carousel.items : null;
  });
  const removedFilledCount = useMemo(() => {
    if (!carouselItems) return 0;
    const removedIds = new Set(removedCarouselItemIds);
    return carouselItems.filter((item) => (
      removedIds.has(String(item.id)) && getCarouselItemSource(item) !== 'empty'
    )).length;
  }, [carouselItems, removedCarouselItemIds]);
  const capacity = useMemo(
    () => (carouselItems ? getCarouselImportCapacity(carouselItems) + removedFilledCount : 0),
    [carouselItems, removedFilledCount],
  );
  const occupiedKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!carouselItems) return keys;
    const removedIds = new Set(removedCarouselItemIds);
    carouselItems.forEach((item) => {
      if (removedIds.has(String(item.id))) return;
      const source = getCarouselItemSource(item);
      if (source === 'work' && item.work) keys.add(`work:${String(item.work.id)}`);
      if (source === 'detail' && item.workDetail) keys.add(`detail:${String(item.workDetail.id)}`);
    });
    return keys;
  }, [carouselItems, removedCarouselItemIds]);
  const isAlreadyInCarousel = (selected: SelectedData) => {
    if (!isActive) return false;
    const key = selected.detailId != null
      ? `detail:${String(selected.detailId)}`
      : `work:${String(selected.workId)}`;
    return occupiedKeys.has(key);
  };

  const enqueueMaxAlert = () => {
    enqueueAlert({
      type: AlertType.ALERT,
      position: AlertPosition.TOP,
      content: getAlertMessage('CAROUSEL_IMPORT_MAX', { maxCount: CAROUSEL_MAX_ITEMS }),
    });
  };

  const importToSlot = (
    selected: SelectedData,
    parentWork?: WorkSimpleWithDetails,
    detail?: WorkDetailSimpleData,
  ) => {
    if (!isActive || !standardModal?.title) return;

    if (isBulkImport) {
      if (isAlreadyInCarousel(selected)) return;
      const importStore = useImportStationWorkStore.getState();
      const alreadySelected = importStore.selectedDataList.some((item) => (
        item.workId === selected.workId && item.detailId === selected.detailId
      ));
      if (alreadySelected) {
        importStore.removeSelectedData(selected);
        return;
      }
      if (importStore.selectedDataList.length >= capacity) {
        enqueueMaxAlert();
        return;
      }
      importStore.addSelectedData({
        ...selected,
        sourceWork: parentWork,
        sourceDetail: detail,
      });
      return;
    }

    if (standardModal.value == null || isAlreadyInCarousel(selected)) return;
    updateCarouselSlotReference(
      standardModal.title,
      Number(standardModal.value),
      selected,
      parentWork,
      detail,
    );
    clearModal();
  };

  const importSelections = () => {
    if (useModalStore.getState().isClosing) return;
    if (!isBulkImport || !standardModal?.title) return;

    const currentSelections = useImportStationWorkStore.getState().selectedDataList;
    const removedIds = useImportStationWorkStore.getState().removedCarouselItemIds;
    if (currentSelections.length === 0 && removedIds.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }

    if (removedIds.length > 0) {
      removeCarouselItemsByIds(standardModal.title, removedIds);
    }
    const omittedCount = importCarouselSelections(
      standardModal.title,
      currentSelections.map((item) => ({
        selected: item,
        work: item.sourceWork,
        detail: item.sourceDetail,
      })),
    );
    if (omittedCount > 0) enqueueMaxAlert();
    clearFormationSlotImportModalState();
    clearModal();
  };

  const isSelected = (selected: SelectedData) => selectedDataList.some((item) => (
    item.workId === selected.workId && item.detailId === selected.detailId
  ));

  return {
    capacity,
    importSelections,
    importToSlot,
    isActive,
    isAlreadyInCarousel,
    isBulkImport,
    isSelected,
    items: carouselItems ?? [],
    replaceSlotIndex: !isBulkImport && standardModal?.value != null
      ? Number(standardModal.value)
      : null,
    selectedCount: selectedDataList.length,
    occupiedCount: CAROUSEL_MAX_ITEMS - capacity + selectedDataList.length,
  };
}
